import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Square, RotateCcw, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  running:   "bg-[#0D2F4D] text-[#C6D21F]",
  completed: "bg-emerald-600 text-white",
  stopped:   "bg-slate-400 text-white",
  paused:    "bg-amber-500 text-white",
  error:     "bg-red-600 text-white",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export function AutomationRunsPanel({ authHeaders }) {
  const [runs, setRuns] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticking, setTicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/admin/automations/runs`, { headers: authHeaders, params });
      setRuns(r.data.runs || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [authHeaders, statusFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const stopRun = async (id) => {
    if (!window.confirm("Stop this run? The contact exits the sequence immediately.")) return;
    try {
      await axios.post(`${API}/admin/automations/runs/${id}/stop`, {}, { headers: authHeaders });
      toast.success("Run stopped."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const retryRun = async (id) => {
    try {
      await axios.post(`${API}/admin/automations/runs/${id}/retry`, {}, { headers: authHeaders });
      toast.success("Run resumed — worker will pick it up on the next tick."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const tickNow = async () => {
    setTicking(true);
    try {
      const r = await axios.post(`${API}/admin/automations/tick`, {}, { headers: authHeaders });
      toast.success(`Tick complete — ${r.data.processed} run(s) processed.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setTicking(false); }
  };

  return (
    <div className="space-y-5" data-testid="automation-runs-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-[#0D2F4D]">Automation runs</h3>
          <p className="text-sm text-[#6F8FA8]">Every contact currently moving through a sequence — the worker processes due steps every minute.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="runs-refresh">
            <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`}/>Refresh
          </Button>
          <Button size="sm" onClick={tickNow} disabled={ticking} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="runs-tick-now">
            <Zap size={13} className="mr-1"/>{ticking ? "Processing…" : "Process due steps now"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="runs-status-chips">
        {["all", "running", "error", "paused", "completed", "stopped"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            data-testid={`runs-chip-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              statusFilter === s
                ? "bg-[#0D2F4D] text-[#C6D21F] border-[#0D2F4D]"
                : "bg-white text-[#0D2F4D] border-[#E4E7EA] hover:border-[#0D2F4D]"}`}>
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== "all" && <span className="ml-1.5 opacity-70">{counts[s] ?? 0}</span>}
          </button>
        ))}
        <div className="ml-auto">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by contact email…" className="w-64 h-8 text-sm" data-testid="runs-search"/>
        </div>
      </div>

      <Card>
        <Table data-testid="runs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Automation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Next step</TableHead>
              <TableHead>Next action</TableHead>
              <TableHead>Started</TableHead>
              <TableHead className="text-right w-[110px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-[#6F8FA8]" data-testid="runs-empty">
                No runs match this filter. Runs appear here as soon as a trigger fires.
              </TableCell></TableRow>
            )}
            {runs.map(r => {
              const lastHistory = (r.history || [])[r.history?.length - 1];
              return (
                <TableRow key={r.id} data-testid={`run-row-${r.id}`}>
                  <TableCell>
                    <div className="font-semibold text-[#0D2F4D] text-sm">{r.contact_email}</div>
                    <Badge variant="outline" className="text-[10px] mt-0.5">{r.brand}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#0D2F4D]">{r.automation_name}</div>
                    <div className="text-[10px] text-[#6F8FA8] font-mono">{r.trigger_type || ""}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_STYLES[r.status] || "bg-slate-400"} data-testid={`run-status-${r.id}`}>{r.status}</Badge>
                    {r.stopped_reason && <div className="text-[10px] text-[#6F8FA8] mt-0.5">{r.stopped_reason.replace(/_/g, " ")}</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.status === "completed" ? `${r.total_steps}/${r.total_steps}` : `${Math.min(r.current_step_index, r.total_steps)}/${r.total_steps}`}
                    {lastHistory && <div className="text-[10px] text-[#6F8FA8] max-w-[180px] truncate" title={lastHistory.result}>{lastHistory.result}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-[#0D2F4D]">
                    {r.status === "error"
                      ? <span className="text-red-700 max-w-[200px] block truncate" title={r.error} data-testid={`run-error-${r.id}`}>{r.error}</span>
                      : (r.next_step || "—")}
                  </TableCell>
                  <TableCell className="text-xs text-[#6F8FA8]">{r.status === "running" ? fmtTime(r.next_action_at) : "—"}</TableCell>
                  <TableCell className="text-xs text-[#6F8FA8]">{fmtTime(r.started_at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {["running", "paused", "error"].includes(r.status) && (
                      <Button size="sm" variant="ghost" onClick={() => stopRun(r.id)} className="text-red-700" title="Stop run" data-testid={`run-stop-${r.id}`}>
                        <Square size={12}/>
                      </Button>
                    )}
                    {["error", "paused"].includes(r.status) && (
                      <Button size="sm" variant="ghost" onClick={() => retryRun(r.id)} className="text-emerald-700" title="Resume / retry" data-testid={`run-retry-${r.id}`}>
                        <RotateCcw size={12}/>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
